import { useEffect, useMemo, useState } from 'react';
import { Loader2, Move, X } from 'lucide-react';

import TableShapeIcon from '../../tables/components/TableShapeIcon';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import ActionButton from '../../../../components/Buttons/ActionButton';

import { RestaurantDiningRoomDTO } from '../../../../types/restaurant/RestaurantDiningRoomTypes';
import { RestaurantTableDTO } from '../../../../types/restaurant/RestaurantTableTypes';

interface Props {
  open: boolean;
  rooms: RestaurantDiningRoomDTO[];
  currentTableId?: number | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadTables: (filters: {
    diningRoomId?: number;
    search?: string;
  }) => Promise<RestaurantTableDTO[]>;
  onConfirm: (targetTableId: number) => Promise<unknown> | void;
}

const OrderTableTransferModal = ({
  open,
  rooms,
  currentTableId,
  busy,
  onOpenChange,
  onLoadTables,
  onConfirm,
}: Props) => {
  const [roomId, setRoomId] = useState<number | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tables, setTables] = useState<RestaurantTableDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setRoomId('ALL');
      setSearch('');
      setDebouncedSearch('');
      setSelectedId(null);
    }
  }, [open]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    onLoadTables({
      diningRoomId: roomId === 'ALL' ? undefined : roomId,
      search: debouncedSearch.trim() || undefined,
    })
      .then((data) => {
        if (!cancelled) {
          setTables(data.filter((table) => table.id !== currentTableId));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, roomId, debouncedSearch, currentTableId, onLoadTables]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  const roomOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Todos los salones' },
      ...rooms.map((room) => ({ value: room.id, label: room.name })),
    ],
    [rooms]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[8999] flex items-center justify-center bg-black/60 px-3 py-6 backdrop-blur-[2px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-table-transfer-title"
        className="flex max-h-[88vh] w-full max-w-lg flex-col rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
      >
        <header className="flex items-center justify-between border-b border-stroke px-5 py-4 dark:border-strokedark">
          <div>
            <h2
              id="order-table-transfer-title"
              className="font-semibold text-black dark:text-white"
            >
              Mover de mesa
            </h2>
            <p className="mt-1 text-xs text-bodydark2">
              Traslade esta orden a una mesa libre. La mesa actual quedará
              disponible.
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="grid grid-cols-1 gap-3 border-b border-stroke p-4 dark:border-strokedark sm:grid-cols-2">
          <CompactSelectField
            label="Salón"
            name="transferRoom"
            value={roomId}
            withMargin={false}
            options={roomOptions}
            onChange={(event) =>
              setRoomId(
                event.target.value === 'ALL'
                  ? 'ALL'
                  : Number(event.target.value)
              )
            }
          />
          <SearchInputField
            label="Buscar mesa"
            name="transferTableSearch"
            value={search}
            placeholder="Nombre o código..."
            withMargin={false}
            size="sm"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="max-h-[42vh] flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {tables.map((table) => (
                <button
                  key={table.id}
                  type="button"
                  onClick={() => setSelectedId(table.id)}
                  className={`rounded-xl border p-3 text-left transition ${
                    selectedId === table.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-stroke hover:border-primary/50 hover:bg-primary/5 dark:border-strokedark'
                  }`}
                >
                  <TableShapeIcon
                    shape={table.shape}
                    capacity={table.capacity}
                    className="mb-1.5 h-7 w-7 text-primary"
                  />
                  <p className="font-semibold text-black dark:text-white">
                    {table.name}
                  </p>
                  <p className="text-xs text-bodydark2">
                    {table.capacity} personas
                  </p>
                </button>
              ))}

              {!tables.length && (
                <p className="col-span-full py-10 text-center text-sm text-bodydark2">
                  No hay mesas libres con esos filtros.
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="grid grid-cols-2 gap-3 border-t border-stroke p-4 dark:border-strokedark">
          <ActionButton
            label="Cancelar"
            variant="outline"
            size="md"
            fullWidth
            onClick={() => onOpenChange(false)}
          />
          <ActionButton
            label="Trasladar"
            icon={<Move className="h-4 w-4" />}
            variant="primary"
            size="md"
            fullWidth
            loading={busy}
            disabled={!selectedId || busy}
            onClick={async () => {
              if (selectedId) await onConfirm(selectedId);
            }}
          />
        </footer>
      </section>
    </div>
  );
};

export default OrderTableTransferModal;
